import React from "react";
import {FormControl, InputAdornment, InputLabel, MenuItem, Select, Stack} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CodeIcon, Message01Icon, TextIcon, UserIcon} from "@hugeicons/core-free-icons";
import {menuItemsInfo} from "../editMenuItems";
import {fieldSx} from "../editMenuStyles";
import {UI} from "../../uiTokens";
import {SectionHeader} from "../components/EditMenuSection";
import {VariableTextField} from "../components/VariableTextField";

// Icon at the start of a field; `top` keeps it on the first line of a multiline field
const fieldIcon = (icon, top = false) => (
    <InputAdornment position="start" sx={top ? {alignSelf: "flex-start", mt: "5px"} : undefined}>
        <HugeiconsIcon icon={icon} size={16} color={UI.muted}/>
    </InputAdornment>
);

// the label stays above the field so it does not sit on top of the icon
const labelProps = {shrink: true};

// The only template for now; more are added here once there is a source for them.
// The default is stored as template_name null; a select value cannot be null, so it is shown under this key.
const DEFAULT_TEMPLATE = "default";
const TEMPLATES = [{name: DEFAULT_TEMPLATE, label: "Default template"}];

export const MailConnectorTab = ({node, setNode, onUpdate, project, flow}) => {
    const variables = project?.flow_variables?.[flow?.id] || [];
    const {primary, secondary} = menuItemsInfo.connector_mail;

    const onChangeData = (key, value) => {
        setNode(prev => ({...prev, data: {...prev.data, [key]: value}}));
    };

    // null (the default) and a saved name that is no longer offered both show as the default
    const templateValue = TEMPLATES.some(t => t.name === node?.data?.template_name) ? node.data.template_name : DEFAULT_TEMPLATE;

    return (
        <>
            <SectionHeader title={primary} description={secondary}/>

            <Stack gap={1.5}>
                <VariableTextField
                    value={node?.data?.to || ""}
                    variables={variables}
                    size="small"
                    label="Recipient"
                    InputLabelProps={labelProps}
                    InputProps={{startAdornment: fieldIcon(UserIcon)}}
                    placeholder="name@example.com or {{customer_email}}"
                    helperText="The recipient address. Type {{ to use a flow variable."
                    sx={fieldSx}
                    onChange={(value) => onChangeData("to", value)}
                    onBlur={() => onUpdate(node)}
                />

                <VariableTextField
                    value={node?.data?.subject || ""}
                    variables={variables}
                    size="small"
                    label="Subject"
                    InputLabelProps={labelProps}
                    InputProps={{startAdornment: fieldIcon(TextIcon)}}
                    sx={fieldSx}
                    onChange={(value) => onChangeData("subject", value)}
                    onBlur={() => onUpdate(node)}
                />

                <VariableTextField
                    value={node?.data?.message || ""}
                    variables={variables}
                    size="small"
                    label="Message"
                    InputLabelProps={labelProps}
                    InputProps={{startAdornment: fieldIcon(Message01Icon, true)}}
                    multiline
                    minRows={5}
                    maxRows={14}
                    helperText="Placed in the template where {{content}} is. Type {{ to use a flow variable."
                    sx={fieldSx}
                    onChange={(value) => onChangeData("message", value)}
                    onBlur={() => onUpdate(node)}
                />

                <FormControl size="small" sx={fieldSx}>
                    <InputLabel id="mail-connector-template-label">HTML template</InputLabel>
                    <Select
                        labelId="mail-connector-template-label"
                        value={templateValue}
                        label="HTML template"
                        startAdornment={fieldIcon(CodeIcon)}
                        onChange={(e) => {
                            const templateName = e.target.value === DEFAULT_TEMPLATE ? null : e.target.value;
                            const updated = {...node, data: {...node?.data, template_name: templateName}};
                            setNode(updated);
                            onUpdate(updated);
                        }}
                    >
                        {TEMPLATES.map(template => (
                            <MenuItem key={template.name} value={template.name}>{template.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
        </>
    );
};
